package co.maytes.standupbot.dashboard.features.releases

import co.maytes.standupbot.shared.dto.ship.ShipEventDto
import kotlinx.datetime.Instant
import kotlin.time.Duration.Companion.days

enum class KindFilter(val label: String) {
    All("All"),
    Releases("Releases"),
    PullRequests("Pull requests"),
    ;

    fun matches(event: ShipEventDto): Boolean =
        when (this) {
            All -> true
            Releases -> event.isRelease
            PullRequests -> !event.isRelease
        }
}

enum class RangeFilter(val label: String, val days: Int?) {
    Week("7 days", 7),
    Month("30 days", 30),
    Quarter("90 days", 90),
    All("All", null),
    ;

    fun start(now: Instant): Instant? = days?.let { now - it.days }
}

enum class FeedGrouping(val label: String) {
    Date("Date"),
    Repository("Repository"),
}

enum class FeedDensity(val label: String) {
    Comfortable("Comfortable"),
    Compact("Compact"),
}

val ShipEventDto.isRelease: Boolean get() = kind == ShipEventDto.KIND_RELEASE

data class ReleaseFilters(
    val query: String = "",
    val kind: KindFilter = KindFilter.All,
    val range: RangeFilter = RangeFilter.All,
    val repos: Set<String> = emptySet(),
) {
    val isActive: Boolean
        get() = query.isNotBlank() || kind != KindFilter.All || range != RangeFilter.All || repos.isNotEmpty()

    val needsLoadedItems: Boolean
        get() = query.isNotBlank() || kind != KindFilter.All || range != RangeFilter.All || repos.size > 1

    val serverRepo: String? get() = repos.singleOrNull()
}

data class KindCounts(val all: Int, val releases: Int, val pullRequests: Int) {
    fun of(kind: KindFilter): Int =
        when (kind) {
            KindFilter.All -> all
            KindFilter.Releases -> releases
            KindFilter.PullRequests -> pullRequests
        }
}

data class RepoOption(val repo: String, val count: Int?)

data class FeedSubtitle(val strong: String, val tail: String)

object ReleaseFeed {
    private val whitespace = Regex("\\s+")
    private const val WEEK_DAYS = 7
    private const val LATEST_RELEASES = 4

    fun matchesQuery(
        event: ShipEventDto,
        query: String,
    ): Boolean {
        val terms = query.lowercase().split(whitespace).filter { it.isNotEmpty() }
        if (terms.isEmpty()) return true
        val haystack = "${event.title} ${event.authorLogin} ${event.ref} ${event.repo}".lowercase()
        return terms.all { it in haystack }
    }

    fun filter(
        items: List<ShipEventDto>,
        filters: ReleaseFilters,
        now: Instant,
    ): List<ShipEventDto> = items.filter { filters.kind.matches(it) && passesWithoutKind(it, filters, now) }

    fun kindCounts(
        items: List<ShipEventDto>,
        filters: ReleaseFilters,
        now: Instant,
    ): KindCounts {
        val scoped = items.filter { passesWithoutKind(it, filters, now) }
        val releases = scoped.count { it.isRelease }
        return KindCounts(all = scoped.size, releases = releases, pullRequests = scoped.size - releases)
    }

    fun repoOptions(
        items: List<ShipEventDto>,
        knownRepos: Collection<String>,
        filters: ReleaseFilters,
        now: Instant,
        serverScope: String?,
    ): List<RepoOption> {
        val scoped = items.filter { filters.kind.matches(it) && passesWithoutRepo(it, filters, now) }
        val counts = scoped.groupingBy { it.repo }.eachCount()
        val repos = (knownRepos + filters.repos + items.map { it.repo }).distinct()
        return repos
            .map { repo ->
                val countable = serverScope == null || serverScope == repo
                RepoOption(repo, if (countable) counts[repo] ?: 0 else null)
            }
            .sortedWith(compareByDescending<RepoOption> { it.count ?: -1 }.thenBy { it.repo.lowercase() })
    }

    fun latestReleases(
        items: List<ShipEventDto>,
        limit: Int = LATEST_RELEASES,
    ): List<ShipEventDto> = items.filter { it.isRelease }.sortedByDescending { it.occurredAt }.take(limit)

    fun repoCount(items: List<ShipEventDto>): Int = items.map { it.repo }.distinct().size

    fun subtitle(
        items: List<ShipEventDto>,
        now: Instant,
        hasMore: Boolean,
        relativeLatest: String,
    ): FeedSubtitle? {
        if (items.isEmpty()) return null
        val weekStart = now - WEEK_DAYS.days
        val recent = items.filter { it.occurredAt >= weekStart }
        if (recent.isEmpty()) {
            return FeedSubtitle(strong = "Nothing", tail = " shipped in the last 7 days. The latest change was $relativeLatest.")
        }
        val truncated = hasMore && items.minOf { it.occurredAt } >= weekStart
        val repos = repoCount(recent)
        val where = if (repos == 1) "in ${recent.first().repo}" else "across $repos repositories"
        val noun = plural(recent.size, "change")
        val strong = (if (truncated) "At least " else "") + "${recent.size} $noun"
        return FeedSubtitle(strong = strong, tail = " shipped in the last 7 days $where")
    }

    fun resultCount(
        shown: Int,
        loaded: Int,
        hasMore: Boolean,
        filtersActive: Boolean,
    ): String =
        when {
            filtersActive -> "Showing $shown of $loaded" + if (hasMore) " loaded" else ""
            hasMore -> "$loaded ${plural(loaded, "change")} loaded"
            else -> "$loaded ${plural(loaded, "change")}"
        }

    fun partialFilterNote(
        items: List<ShipEventDto>,
        filters: ReleaseFilters,
        now: Instant,
        hasMore: Boolean,
    ): String? {
        if (!hasMore || !filters.needsLoadedItems) return null
        val rangeStart = filters.range.start(now)
        val covered = rangeStart != null && items.isNotEmpty() && items.minOf { it.occurredAt } < rangeStart
        if (covered) return null
        return "Filtered from the ${items.size} loaded ${plural(items.size, "change")} so far, the rest is still loading"
    }

    fun plural(
        count: Int,
        noun: String,
    ): String = if (count == 1) noun else "${noun}s"

    private fun passesWithoutKind(
        event: ShipEventDto,
        filters: ReleaseFilters,
        now: Instant,
    ): Boolean = (filters.repos.isEmpty() || event.repo in filters.repos) && passesWithoutRepo(event, filters, now)

    private fun passesWithoutRepo(
        event: ShipEventDto,
        filters: ReleaseFilters,
        now: Instant,
    ): Boolean {
        val start = filters.range.start(now)
        return (start == null || event.occurredAt >= start) && matchesQuery(event, filters.query)
    }
}
